import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #ffffff;
  padding: 40px 24px;
`;

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
  animation: ${fadeIn} 0.3s ease;
`;

const Title = styled.h1`
  color: #fff;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 8px;
  letter-spacing: -0.5px;
  span { color: #fe88f1; }
`;

const Subtitle = styled.p`
  color: rgba(255,255,255,0.4);
  text-align: center;
  font-size: 0.88rem;
  margin: 0 0 32px;
  line-height: 1.5;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.4);
  margin-bottom: 7px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255,255,255,0.05);
  border: 1.5px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  font-family: inherit;
  box-sizing: border-box;
  margin-bottom: 18px;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: rgba(254,136,241,0.5); }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const Btn = styled.button`
  width: 100%;
  padding: 13px;
  background: #fe88f1;
  color: #0d0d0d;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SuccessBox = styled.div`
  background: rgba(0,210,100,0.1);
  border: 1px solid rgba(0,210,100,0.25);
  border-radius: 10px;
  padding: 18px;
  text-align: center;
  animation: ${fadeIn} 0.3s ease;
`;

const SuccessText = styled.p`
  color: #4dde99;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 4px;
`;

const SuccessSub = styled.p`
  color: rgba(255,255,255,0.4);
  font-size: 0.82rem;
  margin: 0;
  line-height: 1.5;
`;

const ErrorMsg = styled.p`
  color: #ff7e7e;
  font-size: 0.85rem;
  text-align: center;
  margin: 0 0 12px;
`;

const BackLink = styled.p`
  color: rgba(255,255,255,0.4);
  font-size: 0.85rem;
  text-align: center;
  margin-top: 20px;
  a { color: #fe88f1; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Helmet>
        <title>Forgot Password — MyCinemaDB</title>
        <meta name="description" content="Reset your MyCinemaDB account password." />
      </Helmet>

      <Card>
        <Title>My<span>Cinema</span>DB</Title>
        <Subtitle>Enter your email and we'll send you a link to reset your password.</Subtitle>

        {sent ? (
          <SuccessBox>
            <SuccessText>📬 Check your inbox!</SuccessText>
            <SuccessSub>
              If an account exists for <strong style={{ color: '#fff' }}>{email}</strong>, a
              reset link has been sent. It expires in 1 hour.
            </SuccessSub>
          </SuccessBox>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <ErrorMsg>{error}</ErrorMsg>}
            <FieldLabel htmlFor="fp-email">Email address</FieldLabel>
            <Input
              id="fp-email"
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              required
              autoComplete="email"
              autoFocus
            />
            <Btn type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send Reset Link'}
            </Btn>
          </form>
        )}

        <BackLink>
          Remember it? <Link to="/sign-in">Back to Sign In</Link>
        </BackLink>
      </Card>
    </Container>
  );
};

export default ForgotPassword;
